// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Transport session. Wraps trystero (nostr strategy) into typed send/receive
 * actions for the sync protocol. trystero discovers the peer over public nostr
 * relays and encrypts the signalling handshake with the room passphrase, so the
 * relay only sees ciphertext. The files themselves ride a direct WebRTC data
 * channel (DTLS on the wire) — no server is ever in the file path.
 *
 * Four actions:
 *   hello  — announce name/direction/mode (JSON)
 *   manif  — a folder manifest: [{path,size,mtime}] (JSON)
 *   req    — a pull request: [path, ...] (JSON)
 *   file   — one encrypted file frame (binary; chunked + progressed by trystero)
 */
import { joinRoom, selfId } from 'trystero/nostr';
import type { Room, BaseRoomConfig, JsonValue, MessageAction } from '@trystero-p2p/core';
import type { Direction, FileEntry } from './types.ts';

const APP_ID = 'syncwell-v1-benrichardson-dev';

export interface Hello {
  name: string;
  direction: Direction;
  kind: 'directory' | 'files';
}

export interface SessionHandlers {
  onPeerJoin: (peerId: string) => void;
  onPeerLeave: (peerId: string) => void;
  onHello: (peerId: string, hello: Hello) => void;
  onManifest: (peerId: string, entries: FileEntry[]) => void;
  onRequest: (peerId: string, paths: string[]) => void;
  onFile: (peerId: string, frame: Uint8Array) => void;
  onFileProgress: (peerId: string, progress: number, path: string) => void;
  onError: (message: string) => void;
}

export class SyncSession {
  readonly selfId = selfId;
  private room: Room | null = null;
  private helloA: MessageAction<JsonValue> | null = null;
  private manifA: MessageAction<JsonValue> | null = null;
  private reqA: MessageAction<JsonValue> | null = null;
  private fileA: MessageAction<Uint8Array> | null = null;

  constructor(
    private roomId: string,
    private pass: string,
    private handlers: SessionHandlers,
  ) {}

  /** Join the room and wire the actions. Returns false if joining threw. */
  join(): boolean {
    const config: BaseRoomConfig = { appId: APP_ID };
    if (this.pass) config.password = this.pass;
    let room: Room;
    try {
      room = joinRoom(config, this.roomId);
    } catch (e) {
      this.handlers.onError(`Could not join: ${(e as Error).message}`);
      return false;
    }
    this.room = room;

    this.helloA = room.makeAction<JsonValue>('hello');
    this.manifA = room.makeAction<JsonValue>('manif');
    this.reqA = room.makeAction<JsonValue>('req');
    this.fileA = room.makeAction<Uint8Array>('file');

    this.helloA.onMessage = (data, ctx) => this.handlers.onHello(ctx.peerId, data as unknown as Hello);
    this.manifA.onMessage = (data, ctx) => this.handlers.onManifest(ctx.peerId, data as unknown as FileEntry[]);
    this.reqA.onMessage = (data, ctx) => this.handlers.onRequest(ctx.peerId, (data as unknown as string[]) || []);
    this.fileA.onMessage = (data, ctx) => this.handlers.onFile(ctx.peerId, data as Uint8Array);
    this.fileA.onReceiveProgress = (progress, ctx) =>
      this.handlers.onFileProgress(ctx.peerId, progress, typeof ctx.metadata === 'string' ? ctx.metadata : '');

    room.onPeerJoin = (peerId) => this.handlers.onPeerJoin(peerId);
    room.onPeerLeave = (peerId) => this.handlers.onPeerLeave(peerId);
    return true;
  }

  sendHello(hello: Hello, target?: string): void {
    this.helloA?.send(hello as unknown as JsonValue, target ? { target } : undefined).catch(() => undefined);
  }

  sendManifest(entries: FileEntry[], target?: string): void {
    this.manifA?.send(entries as unknown as JsonValue, target ? { target } : undefined).catch(() => undefined);
  }

  sendRequest(paths: string[], target: string): void {
    this.reqA?.send(paths as unknown as JsonValue, { target }).catch(() => undefined);
  }

  /** Send one encrypted file frame to a peer, reporting 0..1 upload progress.
   *  The path rides as trystero metadata so receive-progress can be attributed. */
  async sendFile(target: string, frame: Uint8Array, path: string, onProgress?: (p: number) => void): Promise<void> {
    if (!this.fileA) throw new Error('not joined');
    await this.fileA.send(frame, {
      target,
      metadata: path,
      onProgress: onProgress ? (p) => onProgress(p) : undefined,
    });
  }

  getPeerIds(): string[] {
    return this.room ? Object.keys(this.room.getPeers()) : [];
  }

  leave(): void {
    if (!this.room) return;
    try {
      void this.room.leave();
    } catch {
      /* already left */
    }
    this.room = null;
  }
}
